import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '@/lib/api-client';

/**
 * uKnowva data sync — mirrors /api/hrms-sync/*.
 *
 * Every master, custom master and allow-listed table the connected uKnowva
 * instance exposes is a "source". A run fetches the chosen sources into their
 * own staging tables and imports the ones whose column mapping is confirmed
 * into payroll's masters (shifts, leave types, departments, …). A source that
 * fails is reported and the run carries on with the rest.
 */

export type SourceType = 'core' | 'standard' | 'custom' | 'table';
export type MappingStatus = 'none' | 'confirmed' | 'auto_ready' | 'needs_review';
export type SourceRunStatus = 'PENDING' | 'RUNNING' | 'OK' | 'PARTIAL' | 'FAILED' | 'SKIPPED';
export type RunStatus =
  | 'QUEUED' | 'RUNNING' | 'CANCELLING'
  | 'COMPLETED' | 'COMPLETED_WITH_ERRORS' | 'FAILED' | 'CANCELLED';
export type ImportStatus =
  | 'IMPORTED' | 'STORED_ONLY' | 'NEEDS_MAPPING' | 'BLOCKED' | 'FAILED' | 'SKIPPED';

export const ACTIVE_RUN: RunStatus[] = ['QUEUED', 'RUNNING', 'CANCELLING'];

export type RunSummary = {
  id: string;
  status: RunStatus;
  trigger: string;
  triggered_by: string | null;
  summary: string | null;
  error_count: number;
  warning_count: number;
  sources_total: number;
  sources_done: number;
  sources_failed: number;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  heartbeat_at: string | null;
  has_log_file: boolean;
};

export type SyncStatus = {
  connected: boolean;
  reason: string | null;
  instance: string | null;
  mode: 'multi' | 'single';
  employee_field_map_confirmed: boolean;
  sources_known: number;
  sources_available: number;
  needs_mapping: number;
  active_run_id: string | null;
  last_run: RunSummary | null;
};

export type KindField = {
  name: string;
  label: string;
  type: string;
  required: boolean;
  protected: boolean;
  note: string;
};

export type TargetKind = {
  key: string;
  label: string;
  table: string;
  note: string;
  code_from_record_id: boolean;
  fields: KindField[];
};

export type SyncSource = {
  source_key: string;
  source_type: SourceType;
  menu_id: string | null;
  remote_table: string | null;
  title: string;
  local_table: string;
  target_kind: string | null;
  suggested_kind: string | null;
  column_map: Record<string, string>;
  mapping_confirmed: boolean;
  mapping_confirmed_at: string | null;
  mapping_confirmed_by: string | null;
  mapping_status: MappingStatus;
  is_enabled: boolean;
  is_available: boolean;
  is_new: boolean;
  is_core: boolean;
  has_id: boolean;
  master: string;
  description: string;
  field_count: number;
  column_count: number;
  staged_rows: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  last_synced_at: string | null;
  last_status: string | null;
  last_row_count: number | null;
  last_error: string | null;
};

export type Catalog = {
  instance: string;
  mode: 'multi' | 'single';
  refreshed_at: string | null;
  sources: SyncSource[];
  problems: { call: string; error: string }[];
  new_sources: string[];
  kinds: TargetKind[];
  employee_field_map_confirmed: boolean;
};

export type RemoteField = {
  key: string;
  label: string;
  type: string;
  samples: string[];
  stored_as: string | null;
};

export type MappingField = KindField & {
  remote: string | null;
  suggested: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  score: number | null;
  samples: string[];
};

export type SourceMapping = {
  source_key: string;
  title: string;
  kind: string | null;
  kind_label?: string;
  kind_note?: string;
  code_from_record_id?: boolean;
  confirmed: boolean;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  auto_ready?: boolean;
  fields: MappingField[];
  remote_fields: RemoteField[];
  problems: string[];
};

export type RunSourceProgress = {
  source_key: string;
  seq?: number;
  title: string;
  status: SourceRunStatus;
  source_type?: SourceType;
  target_kind?: string | null;
  fetched?: number;
  stored?: number;
  pages?: number;
  total?: number | null;
  inserted?: number;
  updated?: number;
  unchanged?: number;
  staged_rows?: number;
  withdrawn?: number;
  error?: string | null;
  promote_status?: ImportStatus;
  promote?: {
    kind?: string;
    inserted?: number;
    updated?: number;
    unchanged?: number;
    skipped?: number;
    skip_reasons?: Record<string, number>;
    problems?: string[];
    error?: string;
    mapping?: string;
    logins?: string;
    result?: string;
    /** attendance: one-line account of what reached payroll and what did not */
    note?: string;
    window?: string;
    periods?: Record<string, number>;
    unmatched_users?: string[];
    unmatched_user_count?: number;
    no_pay_group?: string[];
  } | null;
  seconds?: number;
};

export type RunEvent = {
  id: number;
  level: 'INFO' | 'WARNING' | 'ERROR';
  source_key: string | null;
  stage: string;
  message: string;
  detail: Record<string, unknown>;
  at: string | null;
};

export type RunDetail = RunSummary & {
  requested_sources: string[];
  options: Record<string, unknown>;
  sources: RunSourceProgress[];
  events: RunEvent[];
  last_event_id: number;
};

export type RunOptions = {
  include_blocked_users?: boolean;
  promote_users?: boolean;
  promote_attendance?: boolean;
  auto_promote?: boolean;
  attendance_start?: string | null;
  attendance_end?: string | null;
};

export type EmployeeFieldRow = {
  payroll_field: string;
  label: string;
  note: string;
  required: boolean;
  hrms_field: string | null;
  hrms_label: string | null;
  hrms_type: string;
  missing_on_instance: boolean;
  confidence: string | null;
  status: 'confirmed' | 'auto' | 'unmapped';
};

export type EmployeeFieldMap = {
  confirmed: boolean;
  confirmed_meta: { at?: string; by?: string } | null;
  fields: EmployeeFieldRow[];
  available: { code: string; label: string; type: string; exported?: boolean }[];
  unmapped_required: string[];
  problems: string[];
};

export type ReceivedFieldStatus =
  | 'used' | 'not_used' | 'blank' | 'not_sent' | 'not_mapped' | 'not_imported';

/** One mapped payroll field: what uKnowva sent and what payroll made of it. */
export type ReceivedField = {
  payroll_field: string;
  label: string;
  hrms_field: string | null;
  received: unknown;
  received_text: unknown;
  payroll_reads: string | null;
  in_payroll: string | null;
  status: ReceivedFieldStatus;
  reason: string | null;
};

export type EmployeeReceived = {
  connected: boolean;
  message?: string;
  field_map_confirmed?: boolean;
  found?: boolean;
  uknowva_user_id?: string;
  synced_at?: string | null;
  gone_from_uknowva_at?: string | null;
  fields?: ReceivedField[];
  raw?: { key: string; label: string; type: string; value: unknown }[];
};

export type StagedPreview = {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  column_sources: Record<string, string | null>;
  table: string;
  title: string;
};

const KEY = ['hrms-sync'] as const;

const enc = encodeURIComponent;

export function useHrmsSyncStatus(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'status'],
    queryFn: () => api.get<SyncStatus>('/hrms-sync/status'),
    enabled,
    staleTime: 15_000,
    // Keep the dashboard card honest while a run is going.
    refetchInterval: (q) => (q.state.data?.active_run_id ? 5_000 : false),
  });
}

// Asking uKnowva what it exposes costs two live calls, so only the window
// opening and the Refresh button do it. Every other refetch — after a mapping
// is saved, after a run — re-reads what this workspace already knows.
let discoverNext = true;

/** Make the next catalogue fetch ask uKnowva live. */
export function requestDiscovery() {
  discoverNext = true;
}

export function useHrmsCatalog(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'catalog'],
    queryFn: () => {
      const refresh = discoverNext;
      discoverNext = false;
      return api.get<Catalog>('/hrms-sync/catalog', { refresh });
    },
    enabled,
    // Refetch on every mount so opening the window always runs the pending
    // discovery; focus changes are not worth a round-trip.
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** The mapping table for one source. `kind` previews another payroll kind
 *  than the one stored; omit it for the stored kind. */
export function useSourceMapping(sourceKey: string | null, kind?: string | null) {
  return useQuery({
    queryKey: [...KEY, 'mapping', sourceKey, kind ?? '__current__'],
    queryFn: () => api.get<SourceMapping>(
      `/hrms-sync/sources/${enc(sourceKey!)}/mapping`,
      kind ? { kind } : undefined,
    ),
    enabled: !!sourceKey,
    retry: false,
  });
}

export function useUpdateSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      sourceKey: string;
      target_kind?: string | null;
      column_map?: Record<string, string | null>;
      confirm?: boolean;
      is_enabled?: boolean;
    }) => {
      const { sourceKey, ...body } = vars;
      return api.put<SyncSource>(`/hrms-sync/sources/${enc(sourceKey)}`, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useBulkEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { source_keys: string[]; is_enabled: boolean }) =>
      api.post<{ changed: number }>('/hrms-sync/sources/enable', vars),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useStagedPreview(sourceKey: string | null, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...KEY, 'preview', sourceKey, page, pageSize],
    queryFn: () => api.get<StagedPreview>(
      `/hrms-sync/sources/${enc(sourceKey!)}/preview`,
      { limit: pageSize, offset: page * pageSize },
    ),
    enabled: !!sourceKey,
  });
}

export function useStartSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { sources?: string[] | null; options?: RunOptions }) =>
      api.post<{ run_id: string; status: RunStatus }>('/hrms-sync/runs', vars),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

/** Earlier OPEN periods whose attendance never fully reached payroll. */
export type AttendanceBackfill = {
  current_window: { start: string; end: string };
  lookback_days: number;
  older_open_periods: number;
  owed: { calendar_id: string; label: string; start: string; end: string }[];
  next: { start: string; end: string; periods: string[] } | null;
  last_attempt: {
    run_id: string; status: SourceRunStatus | null; promote_status: ImportStatus | null;
    start: string; end: string; at: string | null;
  } | null;
  done: boolean;
};

export function useAttendanceBackfill(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'attendance-backfill'],
    queryFn: () => api.get<AttendanceBackfill>('/hrms-sync/attendance-backfill'),
    enabled,
    retry: false,
  });
}

/** Fetch the next slice of older attendance. The server picks the dates. */
export function useStartAttendanceBackfill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ run_id: string; status: RunStatus; start: string; end: string }>(
      '/hrms-sync/attendance-backfill'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useCancelSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.post(`/hrms-sync/runs/${runId}/cancel`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useSyncRuns(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'runs'],
    queryFn: () => api.get<{ runs: RunSummary[] }>('/hrms-sync/runs', { limit: 30 }),
    enabled,
  });
}

/** One poll of a run. The caller accumulates `events` across polls. */
export function fetchRun(runId: string, afterEvent: number) {
  return api.get<RunDetail>(`/hrms-sync/runs/${runId}`, {
    after_event: afterEvent, event_limit: 500,
  });
}

export async function downloadRunLog(runId: string): Promise<void> {
  const text = await apiFetch<string>(`/hrms-sync/runs/${runId}/log`, { method: 'GET' });
  const blob = new Blob([typeof text === 'string' ? text : JSON.stringify(text, null, 2)],
    { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hrms_sync_${runId.slice(0, 8)}.log`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useEmployeeFieldMap(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'employee-field-map'],
    queryFn: () => api.get<EmployeeFieldMap>('/hrms-sync/employee-field-map'),
    enabled,
    retry: false,
  });
}

export function useSaveEmployeeFieldMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mapping: Record<string, string | null>) =>
      api.put<{ confirmed: boolean; mapped: string[] }>(
        '/hrms-sync/employee-field-map', { mapping },
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useSaveConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { instance_url: string; api_token?: string | null }) =>
      api.put<{ instance_url: string; token_stored: boolean }>('/hrms-sync/connection', vars),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

// ── Schedules (the nightly sync) ─────────────────────────────────────────

export type SyncSchedule = {
  name: string;
  label: string;
  description: string;
  is_enabled: boolean;
  run_at: string;
  timezone: string;
  lookback_days: number;
  include_today: boolean;
  sources: string[];
  last_run_on: string | null;
  last_run: RunSummary | null;
  last_error: string | null;
  next_run_at: string | null;
  next_window: { start: string; end: string };
  updated_by: string | null;
  updated_at: string | null;
};

export type ScheduleSourceOption = {
  source_key: string;
  title: string;
  source_type: SourceType;
  is_available: boolean;
  target_kind: string | null;
  mapping_confirmed: boolean;
};

export type SyncSchedules = {
  schedules: SyncSchedule[];
  sources: ScheduleSourceOption[];
  timezones: string[];
  scheduler: { in_app: boolean; tick_seconds: number };
};

export type ScheduleInput = Pick<SyncSchedule,
  'is_enabled' | 'run_at' | 'timezone' | 'lookback_days' | 'include_today' | 'sources'>;

export function useSyncSchedules(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'schedules'],
    queryFn: () => api.get<SyncSchedules>('/hrms-sync/schedules'),
    enabled,
    retry: false,
  });
}

export function useSaveSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; data: ScheduleInput }) =>
      api.put<SyncSchedule>(`/hrms-sync/schedules/${encodeURIComponent(vars.name)}`, vars.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...KEY, 'schedules'] }); },
  });
}

export function useRunScheduleNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<{ run_id: string; status: string }>(`/hrms-sync/schedules/${encodeURIComponent(name)}/run-now`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

/** The message a failed call should show, whatever shape the error took. */
export function errorText(err: unknown): string {
  const e = err as { detailMessage?: string; message?: string; body?: { detail?: unknown } };
  const d = e?.body?.detail as { message?: string } | string | undefined;
  if (d && typeof d === 'object' && d.message) return d.message;
  return e?.detailMessage || e?.message || String(err);
}

/** What the last sync received from uKnowva for one employee (read from the staged copy). */
export function useEmployeeReceived(employeeId: string, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'employee-received', employeeId],
    queryFn: () => api.get<EmployeeReceived>(`/hrms-sync/employees/${enc(employeeId)}/received`),
    enabled: enabled && !!employeeId,
  });
}
